import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchQuestions, submitAnswersBatch, saveProgress, fetchRawAnswers, fetchProgress, deletePreviousAssessment } from '../../api/assessmentApi';
import { v4 as uuidv4 } from 'uuid';
import './Assessment.css';
import {
  FaSave,
} from "react-icons/fa";
import axios from "axios";

// Remove console.logs in production
  if (process.env.NODE_ENV !== "development") {
    console.log = () => {};
  }

// Custom hook for managing local storage
const useLocalStorage = (userId) => {
  const getStorageKey = (key) => `subjectPart3Assessment${key}_${userId}`;
  const getStoredData = (key, defaultValue = null) => {
    const value = localStorage.getItem(getStorageKey(key));
    if (!value) {
      console.log(`No data found for key "${getStorageKey(key)}"`);
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (err) {
      console.error(`Failed to parse JSON for key "${getStorageKey(key)}":`, err);
      return defaultValue;
    }
  };
  const setStoredData = (key, value) => {
    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(value));
      console.log(`Stored ${key} in localStorage:`, value);
    } catch (err) {
      console.error(`Failed to store data for key "${getStorageKey(key)}":`, err);
    }
  };
  const removeStoredData = (key) => {
    console.log(`Removing ${key} from localStorage`);
    localStorage.removeItem(getStorageKey(key));
  };
  return { getStoredData, setStoredData, removeStoredData };
};

// Custom hook for managing assessment state
const useAssessmentState = () => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answeredOrder, setAnsweredOrder] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredCountByCategory, setAnsweredCountByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastCompletionDate, setLastCompletionDate] = useState(null);
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [sessionId, setSessionId] = useState(null);
  return {
    questions, setQuestions, currentIndex, setCurrentIndex, selectedAnswers, setSelectedAnswers,
    answeredOrder, setAnsweredOrder, answeredCount, setAnsweredCount, answeredCountByCategory,
    setAnsweredCountByCategory, loading, setLoading, error, setError, 
    lastCompletionDate, setLastCompletionDate, submittedAnswers, setSubmittedAnswers, sessionId, setSessionId,
  };
};

// Main component
const SubjectPart3Assessment = ({ userId, isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    questions, setQuestions, currentIndex, setCurrentIndex, selectedAnswers, setSelectedAnswers,
    answeredOrder, setAnsweredOrder, answeredCount, setAnsweredCount, answeredCountByCategory,
    setAnsweredCountByCategory, loading, setLoading, error, setError, 
    lastCompletionDate, setLastCompletionDate, submittedAnswers, setSubmittedAnswers, sessionId, setSessionId,
  } = useAssessmentState();
  const { getStoredData, setStoredData, removeStoredData } = useLocalStorage(userId);

  // Get Part II session ID
   const partIISessionIdRef = useRef(location.state?.subjectSessionId || getStoredData('PartIISessionId'));
 console.log('Initial partIISessionId:', { partIISessionId: partIISessionIdRef.current, locationState: location.state });

   // Track page views when the component mounts or route changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Subject Part III Assessment',
      });
    }
  }, [location]);

  // Check if user can take the assessment
  const canTakeAssessment = () => {
    if (isAdmin) return true;
    if (!lastCompletionDate) return true;
    try {
      const sixMonthsInMs = 6 * 30 * 24 * 60 * 60 * 1000;
      const timeSinceLast = Date.now() - new Date(lastCompletionDate).getTime();
      return timeSinceLast >= sixMonthsInMs;
    } catch (err) {
      console.error('Invalid lastCompletionDate:', lastCompletionDate);
      return true;
    }
  };

  // Load initial assessment data
  useEffect(() => {
    let mounted = true;

    const loadAssessment = async () => {
      if (!mounted) return;

      // Load stored data from localStorage
      const storedCompletion = getStoredData('Completion');
      let storedSessionId = getStoredData('SessionId');
      let initialAnswers = getStoredData('Answers', {});
      let initialOrder = getStoredData('Order', []);
      let initialQuestions = getStoredData('Questions', []);
      let initialIndex = getStoredData('Index', 0);
      let initialTopFacets = getStoredData('TopFacets', []);
      let newSession = false;

      console.log('Initial stored data:', {
        storedCompletion,
        storedSessionId,
        answersCount: Object.keys(initialAnswers).length,
        orderLength: initialOrder.length,
        questionsLength: initialQuestions.length,
        initialIndex,
        initialTopFacets,
      });

      setLastCompletionDate(storedCompletion);

      // Validate session
      const canRetake = canTakeAssessment();
      const hasLocalProgress =
        storedSessionId &&
        Object.keys(initialAnswers).length > 0 &&
        Array.isArray(initialOrder) &&
        Array.isArray(initialQuestions);

      console.log('Session checks:', { isAdmin, canRetake, storedCompletion, hasLocalProgress });

      if ((isAdmin && !hasLocalProgress) || (!isAdmin && canRetake && storedCompletion)) {
        console.log('Starting new session: admin with no progress or completed assessment');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('TopFacets');
        removeStoredData('SessionId');
        removeStoredData('Completion');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
        initialTopFacets = [];
      } else if (hasLocalProgress && !storedCompletion) {
        console.log('Resuming existing incomplete session');
        newSession = false;
      } else {
        console.log('No valid local progress or starting new session');
        storedSessionId = uuidv4();
        newSession = true;
        removeStoredData('Answers');
        removeStoredData('Order');
        removeStoredData('Questions');
        removeStoredData('Index');
        removeStoredData('TopFacets');
        removeStoredData('SessionId');
        initialAnswers = {};
        initialOrder = [];
        initialQuestions = [];
        initialIndex = 0;
        initialTopFacets = [];
      }

      setSessionId(storedSessionId);
      setStoredData('SessionId', storedSessionId);

      // Fetch Part II session ID if missing
      if (!partIISessionIdRef.current) {
    console.log('No partIISessionId found, fetching latest Subject progress');
    try {
      const subjectProgress = await fetchProgress(userId, { assessmentType: 'Subject', fetchLatest: 'true' });
      if (subjectProgress?.sessionId) {
        partIISessionIdRef.current = subjectProgress.sessionId;
        setStoredData('PartIISessionId', partIISessionIdRef.current);
        console.log('Fetched Part II sessionId:', partIISessionIdRef.current);
      } else {
        console.warn('No Part II progress found for user:', userId);
        setError('Please complete Part II before proceeding to Part III.');
        setLoading(false);
        navigate('/dashboard');
        return;
      }
    } catch (err) {
      console.error('Error fetching Part II progress:', err);
      setError('Failed to load Part II session. Please complete Part II first.');
      setLoading(false);
      navigate('/dashboard');
      return;
    }
  }

// Function to compute Part II scores client-side
const computePartIIScores = (answers) => {
  const facetMap = answers.reduce((acc, answer) => {
    const facet = answer.facet || 'Unknown';
    if (!acc[facet]) {
      acc[facet] = {
        scores: [],
        taxonomy: new Set(),
        facetNote: answer.facet_note || null,
      };
    }
    acc[facet].scores.push(answer.score);
    if (answer.taxonomy && Array.isArray(answer.taxonomy)) {
      answer.taxonomy.forEach((tax) => acc[facet].taxonomy.add(tax));
    }
    return acc;
  }, {});

  return Object.entries(facetMap)
    .filter(([_, data]) => data.scores.length > 0)
    .map(([facet, data]) => ({
      _id: facet,
      averageScore: data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length,
      taxonomy: Array.from(data.taxonomy),
      facetNote: data.facetNote,
    }));
};

 // Fetch top facets from Part II results
      let topFacets = location.state?.topFacets || getStoredData('TopFacets', []);
      if (!topFacets || topFacets.length === 0) {
        try {
          // Check for cached Part II raw answers
    const CACHE_KEY = `PartIIRawAnswers_${partIISessionIdRef.current}`;
    let rawAnswers = getStoredData(CACHE_KEY, null);
    if (rawAnswers && !location.state?.justSubmittedSubject) {
      console.log('Using cached Part II raw answers:', rawAnswers);
    } else {
      console.log('Fetching Part II raw answers with:', { userId, partIISessionId: partIISessionIdRef.current, assessmentType: 'Subject' });
      rawAnswers = await fetchRawAnswers(userId, partIISessionIdRef.current, 'Subject');
      setStoredData(CACHE_KEY, rawAnswers); // Cache the raw answers
      console.log('Cached Part II raw answers for session:', partIISessionIdRef.current);
    }

          if (!rawAnswers || rawAnswers.length === 0) {
            throw new Error('No answers found for the Subject assessment');
          }

          const partIIAnswers = rawAnswers.filter((a) => a.category === 'Part II');
          if (partIIAnswers.length === 0) {
            throw new Error('No Part II answers found');
          }

          const partIIScores = computePartIIScores(partIIAnswers);
          console.log('Computed Part II scores:', partIIScores);
          if (!partIIScores || partIIScores.length === 0) {
            throw new Error('No valid Part II scores computed');
          }

          const sortedScores = [...partIIScores].sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0));
          topFacets = [sortedScores[0]._id];
          if (sortedScores.length > 1) {
            const secondHighestScore = sortedScores[1].averageScore;
            topFacets = topFacets.concat(
              sortedScores
                .slice(1)
                .filter((score) => score.averageScore >= secondHighestScore)
                .map((score) => score._id)
            );
          }
          setStoredData('TopFacets', topFacets);
       setStoredData('PartIISessionId', partIISessionIdRef.current);
          console.log('Top facets selected:', topFacets);
        } catch (err) {
          console.error('Error computing Part II scores:', err);
          setError('Failed to load Part II results. Please complete Part II first.');
          setLoading(false);
          navigate('/dashboard');
          return;
        }
      }

      // Fetch questions
      try {
        const data = await fetchQuestions({ status: 'Subject Assessments' });
        if (!data || data.length === 0) {
          throw new Error('No questions returned from the server.');
        }

        const uniqueQuestions = Array.from(new Map(data.map((q) => [q._id, q])).values()).filter(
          (q) => q._id && q.questionText && Array.isArray(q.options) && q.options.length > 0
        );
        if (uniqueQuestions.length === 0) {
          throw new Error('No valid questions with options found.');
        }

        let questionsToSet = [];
        const partIIIQuestions = uniqueQuestions.filter(
          (q) => q.category === 'Part III' && q.subCategory && topFacets.includes(q.subCategory)
        );

        if (!newSession && initialQuestions.length > 0) {
          questionsToSet = initialQuestions
            .map((id) => partIIIQuestions.find((q) => q._id === id))
            .filter((q) => q);
          if (questionsToSet.length === 0) {
            console.warn('Stored questions invalid, resetting.');
          }
        }

        if (questionsToSet.length === 0) {
          if (partIIIQuestions.length === 0) {
            throw new Error('No Part III questions available for selected facets.');
          }
          questionsToSet = [...partIIIQuestions.sort(() => Math.random() - 0.5)];
          setStoredData('Questions', questionsToSet.map((q) => q._id));
          setStoredData('TopFacets', topFacets);
        }

        initialOrder = initialOrder.filter((id) => questionsToSet.some((q) => q._id === id));

        if (mounted) {
          setQuestions(questionsToSet);
          setSelectedAnswers(initialAnswers);
          setAnsweredOrder(initialOrder);
          const newAnsweredCount = questionsToSet.filter(
            (q) => initialAnswers[q._id] !== undefined
          ).length;
          setAnsweredCount(newAnsweredCount);

          let startIndex = initialIndex;
          if (initialOrder.length > 0) {
            const lastAnsweredId = initialOrder[initialOrder.length - 1];
            const lastAnsweredIndex = questionsToSet.findIndex(
              (q) => q._id === lastAnsweredId
            );
            startIndex = lastAnsweredIndex >= 0 ? lastAnsweredIndex : initialIndex;
          }
          startIndex = Math.min(Math.max(startIndex, 0), questionsToSet.length - 1);
          setCurrentIndex(startIndex);

          const categoryCounts = {};
          questionsToSet.forEach((q) => {
            if (initialAnswers[q._id]) {
              categoryCounts[q.category] = (categoryCounts[q.category] || 0) + 1;
            }
          });
          setAnsweredCountByCategory(categoryCounts);

          setStoredData('Answers', initialAnswers);
          setStoredData('Order', initialOrder);
          setStoredData('Index', startIndex);
          setStoredData('TopFacets', initialTopFacets);

          setLoading(false);
          console.log('Loaded assessment:', {
            sessionId: storedSessionId,
            questionsToSet: questionsToSet.map((q) => q._id),
            answeredOrder: initialOrder,
            currentIndex: startIndex,
            selectedAnswers: initialAnswers,
            answeredCount: newAnsweredCount,
            topFacets,
          });
        }
      } catch (err) {
        console.error('Error loading questions:', err.message);
        if (mounted) {
          setError(
            `Failed to load assessment: ${err.message}. Please contact support or try again later.`
          );
          setLoading(false);
        }
      }
    };

    loadAssessment();
    document.body.classList.add('specific-page-part3');
    return () => {
      mounted = false;
      document.body.classList.remove('specific-page-part3');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin, navigate, location.state]);

  // Save progress to local storage
  const handleSaveProgress = () => {
    if (!userId || !sessionId) {
      alert('User ID or session ID is missing. Please log in.');
      return;
    }

    if (lastCompletionDate) {
      console.log('Assessment already completed, skipping save progress');
      alert('Assessment is already completed. No progress to save.');
      return;
    }

    if (answeredCount === 0) {
      console.log('No answers to save');
      alert('No answers provided to save.');
      return;
    }

    try {
      // Validate data before saving
      const answersToSave = { ...selectedAnswers };
      Object.keys(answersToSave).forEach((key) => {
        if (typeof answersToSave[key] !== 'string') {
          console.warn(`Invalid answer for question ${key}:`, answersToSave[key]);
          answersToSave[key] = String(answersToSave[key]);
        }
      });

      setStoredData('SessionId', sessionId);
      setStoredData('Answers', answersToSave);
      setStoredData('Order', answeredOrder);
      setStoredData('Questions', questions.map((q) => q._id));
      setStoredData('Index', currentIndex);
      setStoredData('TopFacets', getStoredData('TopFacets', []));
    setStoredData('PartIISessionId', partIISessionIdRef.current);

      // Verify storage
      console.log('Verifying saved data:');
      console.log('SessionId:', getStoredData('SessionId'));
      console.log('Answers:', getStoredData('Answers'));
      console.log('Order:', getStoredData('Order'));
      console.log('Questions:', getStoredData('Questions'));
      console.log('Index:', getStoredData('Index'));
      console.log('TopFacets:', getStoredData('TopFacets'));
      console.log('PartIISessionId:', getStoredData('PartIISessionId'));

      alert('Progress saved locally!');
      setError('');
    } catch (err) {
      console.error('Error saving progress locally:', err);
      alert(`Failed to save progress: ${err.message}`);
    }
  };

  // Return to dashboard
  const handleReturnToDashboard = () => {
    if (answeredCount > 0) {
      handleSaveProgress();
    }
    navigate('/dashboard', { state: { fromSubjectPart3Scores: true, sessionId } });
  };

 // Submit all answers
const handleAnswerSubmit = async () => {
  const allAnswered = questions.every((q) => selectedAnswers[q._id] !== undefined);
  if (!allAnswered) {
    alert('Please complete all questions before submitting.');
    return;
  }

  const warningMessage = isAdmin
    ? 'UniReady Part III Assessment completed! Retake anytime.'
    : 'UniReady Part III Assessment completed! Retake available in 6 months.';
  alert(warningMessage);

  if (!isAdmin) {
    const confirmationMessage = 'Finalise submission? This action cannot be undone.';
    const confirmSubmit = window.confirm(confirmationMessage);
    if (!confirmSubmit) return;
  }

   // Track the "Submit" event in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'submit_assessment', {
        event_category: 'SubjectPart3Assessment',
        event_label: 'Assessment Submission',
        value: answeredCount,
        session_id: sessionId,
        user_id: userId,
      });
    }

  try {
    if (!userId) {
      throw new Error('User ID is missing. Please log in.');
    }
    if (!sessionId) {
      throw new Error('Session ID is missing. Please restart the assessment.');
    }

    console.log('Submitting answers with:', { userId, sessionId, assessmentType: 'SubjectPart3', answersCount: questions.length });

    // Step 1: Check for prior completion
    let hasPriorData = false;
    try {
      const progress = await fetchProgress(userId, { assessmentType: 'SubjectPart3', fetchLatest: true });
      hasPriorData = progress && progress.isCompleted;
      console.log('Prior progress check:', { hasPriorData, lastCompletionDate: progress?.lastCompletionDate });
    } catch (err) {
      console.warn('Error checking prior progress:', err.message);
    }

    // Step 2: Delete previous assessment data if it exists
    if (hasPriorData || isAdmin) {
      try {
        const deleteResult = await deletePreviousAssessment(userId, 'SubjectPart3');
        console.log('Delete result:', deleteResult);
        if (deleteResult.message === 'No previous assessment data found') {
          if (hasPriorData) {
            console.error('Unexpected: No data deleted despite prior data existing');
            throw new Error('Failed to delete prior data despite existing records');
          }
          console.log('No previous assessment data found, proceeding with submission');
        } else if (deleteResult.message === 'Previous assessment data deleted successfully') {
          console.log('Previous assessment data deleted:', {
            userId,
            deletedAnswers: deleteResult.deletedAnswers,
            deletedProgresses: deleteResult.deletedProgresses,
          });
        } else {
          throw new Error('Unexpected deletion response: ' + JSON.stringify(deleteResult));
        }
      } catch (err) {
        console.error('Error deleting previous assessment data:', err);
        throw new Error('Failed to clear previous assessment data: ' + err.message);
      }
    } else {
      console.log('First attempt detected, no deletion needed');
    }

 const computeTopPartIIISubjects = () => {
  const subjectScores = {};

  questions.forEach(q => {
    if (!q.subCategory) return;

    // Use a more specific name if available
    let subjectName = q.facet || q.subCategory;   // Prefer facet if it exists

    // Optional: Add refinement logic
    // if (q.subCategory === 'Biology' && q.facet) {
    //   subjectName = q.facet;   // e.g. "Molecular Biology"
    // }

    if (!subjectScores[subjectName]) {
      subjectScores[subjectName] = { total: 0, count: 0 };
    }

    const answerText = selectedAnswers[q._id];
    const selectedOption = q.options?.find(opt => opt.optionText === answerText);
    const score = selectedOption ? selectedOption.score : 0;

    subjectScores[subjectName].total += score;
    subjectScores[subjectName].count += 1;
  });

  const sorted = Object.entries(subjectScores)
    .map(([subject, data]) => ({
      subject,                                      // ← This is what gets stored
      averageScore: Number((data.total / data.count).toFixed(2))
    }))
    .sort((a, b) => b.averageScore - a.averageScore);

  return sorted;
};

    // Step 3: Prepare and submit new answers
    const questionOptionsMap = questions.reduce((map, q) => ({
      ...map,
      [q._id]: { options: q.options },
    }), {});
    const answersBatch = questions
      .filter((question) => !submittedAnswers.has(question._id))
      .map((question) => {
        const answer = selectedAnswers[question._id];
        if (!answer) {
          throw new Error(`No answer provided for question ${question._id}`);
        }
        const selectedOption = Array.isArray(question.options)
          ? question.options.find((opt) => opt.optionText === answer)
          : null;
        const score = selectedOption ? selectedOption.score : 0;
        return {
          userId,
          questionId: question._id,
          sessionId,
          category: question.category || 'Part III',
          subCategory: question.subCategory || null,
          facet: question.facet || null,
          taxonomy: question.taxonomy || null,
          score,
          assessmentType: 'SubjectPart3',
        };
      });

    if (answersBatch.length > 0) {
      await submitAnswersBatch(userId, sessionId, answersBatch, 'SubjectPart3', questionOptionsMap);
      answersBatch.forEach((answer) => {
        setSubmittedAnswers((prev) => new Set(prev).add(answer.questionId));
      });
    } else {
      console.log('No new answers to submit. All questions may already be submitted.');
    }

    // Step 4: Save completion data to database
    const completionDate = new Date().toISOString();
    const progressData = {
      userId,
      sessionId,
      assessmentType: 'SubjectPart3',
      topFacets: getStoredData('TopFacets', []),
      lastCompletionDate: completionDate,
    };

    console.log('Saving completion data:', progressData);
    await saveProgress(progressData);


    // Step 5: Update local storage and state
    setStoredData('Completion', completionDate);
    setLastCompletionDate(completionDate);

    removeStoredData('Answers');
    removeStoredData('Order');
    removeStoredData('Questions');
    removeStoredData('Index');
    removeStoredData('TopFacets');
    removeStoredData('SessionId');
    removeStoredData('PartIISessionId');

    // Step 6: Reset state for admin users
    if (isAdmin) {
      setSelectedAnswers({});
      setAnsweredOrder([]);
      setAnsweredCount(0);
      setCurrentIndex(0);
      setSessionId(uuidv4());
      removeStoredData('Completion');
    }

    // Step 7: Clear sessionStorage cache for subject part 3 visualizations
    sessionStorage.removeItem(`visualizations_${userId}_SubjectPart3`);
    sessionStorage.removeItem(`raw_answers_${userId}_SubjectPart3`);

const topPart3 = computeTopPartIIISubjects();

navigate('/dashboard', { 
  state: { 
    justSubmittedSubjectPart3: true, 
    completionDate, 
    sessionId,
    topPart3Subjects: topPart3   // ← NEW: Send scored subjects
  } 
});
  } catch (err) {
    console.error('Error submitting answers:', {
      message: err.message,
      stack: err.stack,
    });
    const errorMessage =
      err.message.includes('Answer already submitted') ||
      err.message.includes('You can only complete this assessment once every 6 months')
        ? 'This assessment has already been completed. You can retake it in 6 months.'
        : err.message.includes('log in') || err.message.includes('User ID is missing')
        ? 'Session expired or invalid credentials. Please log in again.'
        : err.message.includes('Failed to clear previous assessment data')
        ? 'Unable to connect to the server to clear previous data. Please check your connection and try again.'
        : `Failed to submit answers: ${err.message}`;

         // Track submission failure
      if (window.gtag) {
        window.gtag('event', 'submit_assessment_failure', {
          event_category: 'SubjectPart3Assessment',
          event_label: 'Assessment Submission Failed',
          value: answeredCount,
          session_id: sessionId,
          user_id: userId,
          error_message: err.message,
        });
      }
      
    alert(errorMessage);
    if (err.message.includes('log in') || err.message.includes('User ID is missing')) {
      navigate('/login');
    } else {
      navigate('/dashboard');
    }
  }
};


  // Navigate to next question
  const handleNext = () => {
    const currentQuestionId = questions[currentIndex]._id;
    const currentAnswer = selectedAnswers[currentQuestionId];

    if (currentAnswer === undefined || currentAnswer === '') {
      alert('Please select an answer before proceeding.');
      return;
    }

    const currentOrderIndex = answeredOrder.indexOf(currentQuestionId);
    if (currentOrderIndex >= 0 && currentOrderIndex < answeredOrder.length - 1) {
      const nextAnsweredId = answeredOrder[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
        return;
      }
    }

    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length && selectedAnswers[questions[nextIndex]._id]) {
      nextIndex++;
    }

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
    } else if (answeredOrder.length > 0 && currentOrderIndex < answeredOrder.length - 1) {
      const nextAnsweredId = answeredOrder[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
      }
    }
  };

    // Navigate to previous question
  const handlePrevious = () => {
    if (currentIndex === 0) return;

    const currentQuestionId = questions[currentIndex]._id;
    const currentOrderIndex = answeredOrder.indexOf(currentQuestionId);

    // If the current question has already been answered, retrace the
    // user's actual answering path via answeredOrder.
    if (currentOrderIndex > 0) {
      const prevAnsweredId = answeredOrder[currentOrderIndex - 1];
      const prevQuestionIndex = questions.findIndex((q) => q._id === prevAnsweredId);
      if (prevQuestionIndex >= 0) {
        setCurrentIndex(prevQuestionIndex);
        return;
      }
    }

    // Current question hasn't been answered yet — e.g. the user was just
    // auto-advanced onto it — so it won't be in answeredOrder. In that
    // case just step back one position in the question list rather than
    // doing nothing.
    setCurrentIndex(currentIndex - 1);
  };

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) {
      console.error('No current question available');
      setError('No question available. Please try again.');
      return;
    }

    const wasPreviouslyAnswered = !!selectedAnswers[currentQuestion._id];

    setSelectedAnswers((prev) => {
      const newAnswers = { ...prev, [currentQuestion._id]: answer };
      console.log('Updated selectedAnswers:', newAnswers);
      return newAnswers;
    });

    // Track the answered order locally too, since setAnsweredOrder's
    // updater callback below runs async and we need the up-to-date
    // order immediately for auto-advance.
    let orderForAdvance = answeredOrder.includes(currentQuestion._id)
      ? answeredOrder
      : [...answeredOrder, currentQuestion._id];

    if (!wasPreviouslyAnswered) {
      setAnsweredCount((prev) => {
        const newCount = prev + 1;
        console.log('Updated answeredCount:', newCount);
        return newCount;
      });
      setAnsweredOrder((prev) => {
        if (!prev.includes(currentQuestion._id)) {
          const newOrder = [...prev, currentQuestion._id];
          console.log('Updated answeredOrder:', newOrder);
          return newOrder;
        }
        return prev;
      });
      setAnsweredCountByCategory((prev) => {
        const category = currentQuestion.category || 'Part III';
        const newCounts = { ...prev, [category]: (prev[category] || 0) + 1 };
        console.log('Updated answeredCountByCategory:', newCounts);
        return newCounts;
      });
    }

    // Save answer to local storage
    try {
      setStoredData('Answers', { ...selectedAnswers, [currentQuestion._id]: answer });
      setStoredData('Order', orderForAdvance);
      setStoredData('Index', currentIndex);
    } catch (err) {
      console.error('Error saving answer to local storage:', err);
    }

    // Auto-advance to the next question after a brief pause so the
    // selection highlight is visible before the view moves on. Skipped
    // when this is the last question so the Submit button shows.
    if (currentIndex < questions.length - 1) {
      setTimeout(() => {
        advanceToNextQuestion(currentQuestion._id, orderForAdvance);
      }, 300);
    }
  };

  // Advance to the next question, mirroring handleNext's logic but driven
  // directly by the answer that was just selected rather than waiting on
  // React state (selectedAnswers/answeredOrder) to finish updating.
  const advanceToNextQuestion = (currentQuestionId, orderAfterAnswer) => {
    const currentOrderIndex = orderAfterAnswer.indexOf(currentQuestionId);
    if (currentOrderIndex >= 0 && currentOrderIndex < orderAfterAnswer.length - 1) {
      const nextAnsweredId = orderAfterAnswer[currentOrderIndex + 1];
      const nextAnsweredIndex = questions.findIndex((q) => q._id === nextAnsweredId);
      if (nextAnsweredIndex >= 0) {
        setCurrentIndex(nextAnsweredIndex);
        return;
      }
    }

    const currentQuestionIndex = questions.findIndex((q) => q._id === currentQuestionId);
    let nextIndex = currentQuestionIndex + 1;
    while (
      nextIndex < questions.length &&
      selectedAnswers[questions[nextIndex]._id] !== undefined &&
      questions[nextIndex]._id !== currentQuestionId
    ) {
      nextIndex++;
    }

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
    }
  };

  // Render loading, error, or empty states
  if (loading) return <p>Loading UniReady Part III Assessment questions...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!questions.length) {
    console.log('questions is empty after loadAssessment:', questions);
    return (
      <p className="error-message">
        No UniReady Part III Assessment questions available. Please contact support.
      </p>
    );
  }

  // Render assessment UI
  const currentQuestion = questions[currentIndex];
  const currentSelectedAnswer = selectedAnswers[currentQuestion._id] || '';
  const allAnswered = questions.every((q) => selectedAnswers[q._id] !== undefined);

  const showPreviousButton = currentIndex > 0;

  return (
    <div className="specific-page-part3">
      <header className="assessment-header">
        <div className="admin-dashboard-logo logo-adjustment-score"></div>
        <h1 className="assessment-heading">ASPIRE UniReady Psychometric Test - Advanced Subject Exploration</h1>
        <p className="copyright">© 2026 Oxbridge Gateway Ltd., All Rights Reserved.</p>
                        <button className="nav-button-save" onClick={handleSaveProgress}>
                    <FaSave className="save-icon" />
                  </button>
      </header>

      <div className="assessment-container">
        <div className="progress-container">
          <p className="text-subject">
            Advanced Subject Exploration focuses on exploring the highest-ranked general subjects in Part II
          </p>
          <div className="progress-bar">
            <div
              className="progress-fill total-progress"
              style={{
                width: `${(answeredCount / questions.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        <div className="assessment-question-container">
          <h3 className="category-text-subject">Category: {currentQuestion.category}</h3>
          <p className="category-text-subject-progress">
            Progress: {answeredCountByCategory[currentQuestion.category] || 0} /
            {questions.filter((q) => q.category === currentQuestion.category).length}{' '}
            questions
          </p>
          <p className="assessment-question-text-subject">
            {currentQuestion.questionText}
          </p>

          <div className="navigation-container">
            <div className="options-navigation">
              <button
                className={`nav-arrow nav-arrow-left ${showPreviousButton ? 'visible' : ''}`}
                onClick={handlePrevious}
                aria-label="Previous question"
                disabled={!showPreviousButton}
              >
                <svg viewBox="0 0 24 24" className="arrow-icon-subject">
                  <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
              <div className="options-container">
                {Array.isArray(currentQuestion.options) ? (
                  currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      className={`option-button ${
                        currentSelectedAnswer === option.optionText ? 'selected' : ''
                      }`}
                      onClick={() => handleAnswerSelect(option.optionText)}
                    >
                      {option.optionText}
                    </button>
                  ))
                ) : (
                  <p className="error-message">
                    No options available for this question.
                  </p>
                )}
              </div>
              <button
                className={`nav-arrow nav-arrow-right ${
                  currentIndex < questions.length - 1 ? 'visible' : ''
                }`}
                onClick={handleNext}
                aria-label="Next question"
                disabled={currentIndex >= questions.length - 1}
              >
                <svg viewBox="0 0 24 24" className="arrow-icon-subject">
                  <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            {allAnswered && (
              <button className="nav-button" onClick={handleAnswerSubmit}>
                Submit
              </button>
            )}
          </div>
        </div>
        <button
          className="return-dashboard-cross"
          onClick={handleReturnToDashboard}
        ></button>
      </div>
    </div>
  );
};


export default SubjectPart3Assessment;